import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

// UI
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button } from '@chakra-ui/react'

// Component

import { authSelector } from '@/stores/auth/authSlice'
import { camelToWords } from '@/utils/stringHelper'

import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'
import { createICS } from '@/utils/outlook'

//API

import useUser from '@/features/users/useUser'

//Constant
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { getOfferListCommonColumns } from '@/features/offers/constants/offerColumns'
import { SystemVariableType } from '@/features/systemVariables/constants'
import { OfferBookingStatus } from '@eapproval/offer-shared'
import { ApprovalInvitationStatus, ApprovalStatus, ContributorStatus, OfferQuotationStatus } from '../constants'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { applyLinkColumnFilters, convertTableStateToQuery, stripLinkColumnFilters } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import { RowSelectionCell } from '@/components/Table/components/Cell'
import DateCell from '@/components/Table/components/Cell/DateCell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useGCKCode } from '@/features/GCKCodes/useGCKCode'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import OfferApprovalListHeader from '../components/List/OfferApprovalListHeader'
import useOfferList from '../hooks/useOfferList'

const OfferApprovalListPage = () => {
  const { user: currentUser } = useSelector(authSelector)
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const approvalStatus = searchParams.get('approvalStatus')
  const status = searchParams.get('status')

  const initialFilter = useMemo(() => {
    const columnFilters = []
    if (approvalStatus) {
      columnFilters.push({ id: 'approvalStatus', value: approvalStatus.split(',') })
    }

    if (status) {
      columnFilters.push({ id: 'status', value: status.split(',') })
    }

    if (columnFilters.length === 0) {
      return {}
    }

    return { columnFilters: columnFilters }
  }, [approvalStatus, status])

  // Local State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:offerApproval`, {})
  const [linkColumnFilters, setLinkColumnFilters] = useState(null)

  const tableState = useMemo(
    () => applyLinkColumnFilters(storedTableState, linkColumnFilters),
    [storedTableState, linkColumnFilters]
  )

  const setTableState = useCallback(
    (updater) => {
      const nextState = typeof updater === 'function' ? updater(tableState) : updater
      const withoutLinkFilters = stripLinkColumnFilters(nextState, linkColumnFilters, storedTableState?.columnFilters)

      if (withoutLinkFilters) {
        setStoredTableState(withoutLinkFilters)
        return
      }

      setLinkColumnFilters(null)
      setStoredTableState(nextState)
    },
    [linkColumnFilters, setStoredTableState, storedTableState?.columnFilters, tableState]
  )

  const query = convertTableStateToQuery(tableState)
  // Hooks

  const { useFetchOfferApprovals } = useOfferList()
  const { useCostCenterOptions } = useCostCenter()
  const { useGCKCodeOptions } = useGCKCode()
  const { useUserOptions } = useUser()
  const { useSystemVariableOptions } = useSystemVariable()

  const { data: offerApprovalResponse, isRefetching, isError } = useFetchOfferApprovals(query)

  //Options
  const { data: teamOptions } = useSystemVariableOptions(SystemVariableType.TEAM, { status: 'active' })
  const { data: businessUnitOptions } = useSystemVariableOptions(SystemVariableType.BUSINESS_UNIT, { status: 'active' })
  const { data: costCenterOptions } = useCostCenterOptions({ status: 'active', labelType: 'simple' })
  const { data: GCKOptions } = useGCKCodeOptions({ status: 'active', labelType: 'detail' })
  const { data: allUserOptions } = useUserOptions({ status: 'active' })

  const { data: salesUserOptions } = useUserOptions({
    labelKey: 'fullName',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })
  const { data: PMOptions } = useUserOptions({
    status: 'active',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })

  const selectedApprovalDetails = useMemo(() => {
    if (!Array.isArray(offerApprovalResponse?.data)) {
      return []
    }
    const selectedRowDetails = Object.keys(tableState?.rowSelection ?? {})
      .map((index) => {
        const rowDetail = offerApprovalResponse.data[index]
        return {
          approvalType: rowDetail?.approvalType || '',
          offerId: rowDetail?._id || '',
        }
      })
      .filter((detail) => detail?.approvalType !== '' || detail.offerId !== '')
    return selectedRowDetails
  }, [offerApprovalResponse?.data, tableState?.rowSelection])

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()

  const handleGotoOffer = (row) => {
    if (!row?.original?._id) {
      return
    }
    navigate(`/offers/${row?.original?._id}`)
  }

  const defaultColumns = [
    columnHelper.display({
      id: 'select',
      header: () => <></>,
      size: 50,
      cell: (cell) => <RowSelectionCell {...cell} />,
    }),
    columnHelper.display({
      id: 'action',
      size: 80,
      cell: ({ row: { original } }) => {
        return (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={(e) => {
              e.stopPropagation()

              createICS(original.dueDate, original.projectName, original._id, original.quotationNumber)
            }}
          >
            Outlook
          </Button>
        )
      },
    }),
    ...getOfferListCommonColumns({
      salesUserOptions,
      creatorOptions: allUserOptions,
      teamOptions,
      businessUnitOptions,
      PMOptions,
      costCenterOptions,
      GCKOptions,
    }),
    columnHelper.accessor('newOrRenewal', {
      header: () => <span>New / Renewal</span>,

      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'New', value: 'new' },
          { label: 'Renewal', value: 'renewal' },
          { label: 'Renewal Of Discontinued', value: 'renewalOfDiscontinued' },
        ],
      },
    }),
    columnHelper.accessor('approvalType', {
      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'Contribution', value: 'contribution' },
          { label: 'Optional Contribution', value: 'optionalContribution' },
          { label: 'Quotation Approval', value: 'quotationApproval' },
          { label: 'Booking Approval', value: 'bookingApproval' },
          { label: 'Quotation Invitation', value: 'quotationInvitation' },
          { label: 'Booking Invitation', value: 'bookingInvitation' },
          { label: 'Delegation', value: 'delegation' },
        ],
      },
    }),
    columnHelper.accessor('approvalRole', {
      enableColumnFilter: false,
    }),
    columnHelper.accessor('mainSubSystemType', {
      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'Maintenance', value: 'maintenance' },
          { label: 'Project', value: 'project' },
        ],
      },
    }),
    columnHelper.accessor('approvalStatus', {
      cell: ({ row: { original }, getValue }) => {
        let text = camelToWords(getValue())
        let tooltip = text
        if (original?.approvalType === 'quotationInvitation') {
          tooltip = `Invited by ${original?.quotationInviter || 'other user'}`
        } else if (original?.approvalType === 'bookingInvitation') {
          tooltip = `Invited by ${original?.bookingInviter || 'other user'}`
        }
        return <TooltipCell text={text} tooltip={tooltip} />
      },
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(ApprovalStatus)
          .concat([ContributorStatus.REVIEWED, ContributorStatus.COMMENTED])
          .concat([ApprovalInvitationStatus.INVITED, ApprovalInvitationStatus.REPLIED])
          .map((value) => {
            return {
              value: value,
              label: value,
            }
          }),
      },
    }),
    columnHelper.accessor('approvalDueDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
      },
    }),
    columnHelper.accessor('bookingBond', {
      header: () => <span>Bond Completed</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('bookingInsurance', {
      header: () => <span>Insurance Completed</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('bookingEPD', {
      header: () => <span>EPD Completed</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('bookingCIC', {
      header: () => <span>CIC Completed</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('status', {
      cell: ({ getValue, row: { original } }) => {
        let tooltipValue = getValue()

        return <TooltipCell text={getValue()} tooltip={tooltipValue} />
      },
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values({ ...OfferQuotationStatus, ...OfferBookingStatus }).map((status) => {
          return {
            label: camelToWords(status),
            value: status,
          }
        }),
      },
    }),
  ]

  useEffect(() => {
    setStoredTableState((prev) => ({
      ...prev,
      rowSelection: {},
    }))
  }, [tableState?.pagination?.pageIndex])

  const handleClearFilters = useCallback(() => {
    setLinkColumnFilters(null)
    setStoredTableState((prev) => ({
      ...prev,
      columnFilters: [],
      pagination: { ...prev?.pagination, pageIndex: 0 },
    }))
  }, [setStoredTableState])

  useEffect(() => {
    if (Object.keys(initialFilter).length > 0) {
      setLinkColumnFilters(initialFilter.columnFilters)

      navigate(location.pathname, { replace: true })
    }
  }, [initialFilter])

  const checkCanSelectRow = (row) => {
    return (
      !['contribution', 'invitation', 'quotationInvitation', 'bookingInvitation'].includes(
        row?.original?.approvalType
      ) && row?.original?.approvalStatus === ApprovalStatus.REQUIRED
    )
  }

  if (!currentUser?.fullName) {
    return <>Loading...</>
  }

  return (
    <>
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <OfferApprovalListHeader
          offerCount={offerApprovalResponse?.count}
          selectedApprovals={selectedApprovalDetails}
          resetSelection={() => {
            setStoredTableState((prev) => {
              return {
                ...prev,
                rowSelection: {},
              }
            })
          }}
        />
        <Box p={5} overflow={'scroll'}>
          {isError && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              <Box flex="1">
                <AlertTitle>This list could not be loaded</AlertTitle>
                <AlertDescription>
                  A saved filter is no longer valid. Clearing the filters will restore your approvals.
                </AlertDescription>
              </Box>
              <Button size="sm" colorScheme="red" onClick={handleClearFilters}>
                Clear filters
              </Button>
            </Alert>
          )}
          <FullTable
            data={offerApprovalResponse?.data ? offerApprovalResponse.data : []}
            rowCount={offerApprovalResponse?.count}
            columnDef={defaultColumns}
            state={tableState}
            setState={setTableState}
            handleRowClick={handleGotoOffer}
            enableRowSelection={checkCanSelectRow}
            isLoading={isRefetching}
          />
        </Box>
      </Box>
    </>
  )
}

export default OfferApprovalListPage
